import { useState } from "react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { C } from "../../shared/theme";
import { Sortable, DragHandle, useDragSensors } from "../../shared/Sortable";
import { goalColorTokens } from "../../shared/goalColors";
import { dueWords } from "../../shared/deadlines";
import { sortGoalsForView, tasksForGoal, GOAL_VIEWS } from "../tasks/goalOrder";
import { taskWords } from "../../shared/taskWords";
import { RANK_COPY, mergeOrder } from "./ranking";
import { stepTitle, stepIntro } from "./StepFrame";

// The order a ranking list is showing. Help me choose saves it when the user moves on, so an order accepted
// without changes is kept too.
export function useRankOrder(view, goals) {
  const [order, setOrder] = useState(() => sortGoalsForView(goals, view).map(g => g.id));
  const byId = new Map(goals.map(g => [g.id, g]));
  const shown = mergeOrder(order, sortGoalsForView(goals, view).map(g => g.id));
  return { shown, setOrder, goalsInOrder: () => shown.map(id => byId.get(id)), byId };
}

// One ranking step: the user's active goals in importance or urgency order. Move a goal by dragging ⠿, or
// from the keyboard (Space on ⠿, then the arrow keys). The ▲ ▼ buttons went on 20 Oct 2026 (Craig: "The
// drag handles are sufficient"). Each move is saved through onReorder(view, ordered), which returns a
// promise. On the urgency step each goal shows its deadline, since that is usually what urgency is about.
// Tap a goal's name to see its unfinished tasks under it: knowing what a goal involves helps rank it
// (Craig, 20 Oct 2026). Goals start folded.
export function RankStep({ view, rank, failed, setFailed, onReorder, tasks = [] }) {
  const { shown, setOrder, goalsInOrder, byId } = rank;
  const [openIds, setOpenIds] = useState([]);
  const toggle = (id) => setOpenIds(ids => (ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]));
  const sensors = useDragSensors();
  const copy = RANK_COPY[view];

  const apply = (next) => {
    setOrder(next);
    setFailed(false);
    onReorder(view, next.map(id => byId.get(id))).catch(e => { console.error("Saving goal order failed:", e); setFailed(true); });
  };
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    apply(arrayMove(shown, shown.indexOf(active.id), shown.indexOf(over.id)));
  };

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div>
        <h2 style={stepTitle()}>{copy.title}</h2>
        <p style={stepIntro()}>{copy.body}</p>
      </div>
      {failed && (
        <p role="alert" style={{ margin: 0, fontSize: 15, lineHeight: 1.5, color: C.red }}>
          We couldn't save this order. Check your connection and try again.
        </p>
      )}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={shown} strategy={verticalListSortingStrategy}>
          <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 8 }}>
            {goalsInOrder().map((goal, n) => {
              const due = view === GOAL_VIEWS.URGENCY ? dueWords(goal.deadline) : null;
              const allTasks = tasksForGoal(tasks, goal.id);
              const goalTasks = allTasks.filter(t => !t.done);
              // A goal with nothing left to do has nothing to open: its line says so.
              const canOpen = goalTasks.length > 0;
              const open = canOpen && openIds.includes(goal.id);
              const listId = `rank-tasks-${view}-${goal.id}`;
              const Name = canOpen ? "button" : "div";
              return (
                <li key={goal.id}>
                  <Sortable id={goal.id}>
                    {(drag) => (
                      <div style={{ background: C.surface, border: `1px solid ${drag.isDragging ? C.accent : C.borderCard}`, borderRadius: 12, boxShadow: drag.isDragging ? C.shadowMd : "none" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 56, padding: "0 2px 0 12px" }}>
                        <span style={{ minWidth: 20, fontSize: 15, fontWeight: 800, color: C.textMid }}>{n + 1}</span>
                        <span aria-hidden="true" style={{ width: 4, alignSelf: "stretch", margin: "10px 0", borderRadius: 4, background: goalColorTokens(goal.color).main, flexShrink: 0 }} />
                        <Name {...(canOpen ? { onClick: () => toggle(goal.id), "aria-expanded": open, "aria-controls": open ? listId : undefined } : {})}
                          style={{ flex: 1, minWidth: 0, padding: "10px 0", background: "none", border: "none", textAlign: "left", cursor: canOpen ? "pointer" : "default", fontFamily: "inherit" }}>
                          <span style={{ display: "block", fontSize: 16, fontWeight: 600, lineHeight: 1.3, color: C.text }}>{goal.title}</span>
                          <span style={{ display: "block", marginTop: 2, fontSize: 13, color: C.textMid }}>
                            {due && <span style={{ color: due.overdue ? C.red : C.textMid }}>{due.text} · </span>}
                            {taskWords(allTasks)}{canOpen && <span aria-hidden="true" style={{ fontSize: 11 }}> {open ? "▲" : "▼"}</span>}
                          </span>
                        </Name>
                        <DragHandle label={goal.title} {...drag} />
                      </div>
                      {open && (
                        <ul id={listId} style={{ listStyle: "none", margin: 0, padding: "0 14px 10px 44px", display: "grid", gap: 6 }}>
                          {goalTasks.map(t => <li key={t.id} style={{ fontSize: 15, lineHeight: 1.4, color: C.text }}>· {t.text}</li>)}
                        </ul>
                      )}
                      </div>
                    )}
                  </Sortable>
                </li>
              );
            })}
          </ol>
        </SortableContext>
      </DndContext>
    </div>
  );
}
